import { useEffect, useState } from "react";
import Select from "react-select";

const PlanShiftModal = ({
    setup,
    machines = [],
    products = [],
    dies = [],
    loading,
    listLoading,
    onClose,
    onCreate,
    onUpdate,
}) => {

    // =========================================================
    // FORM DATA
    // =========================================================

    const [formData, setFormData] = useState({
        machine_id: "",
        product_id: "",
        die_id: "",
        shift_id: "",
        notes: "",
        status: "planed",
        plan_date: "",
    });

    const [error, setError] = useState("");

// =========================================================
// FORMAT DATE FOR HTML DATE INPUT
// =========================================================
const formatDateForInput = (date) => {
    if (!date) return "";

    // If already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
    }

    // Handle Laravel datetime / ISO date
    return String(date).substring(0, 10);
};
    // =========================================================
    // LOAD DATA FOR EDIT
    // =========================================================

    useEffect(() => {

        if (setup) {

            setFormData({
                machine_id: setup.machine_id ?? "",
                product_id: setup.product_id ?? "",
                die_id: setup.die_id ?? "",
                shift_id: setup.shift_id ?? "",
                notes: setup.notes ?? "",
                status: setup.status ?? "planed",
                  // IMPORTANT
                plan_date: formatDateForInput(setup.plan_date),
            });

        } else {

            setFormData({
                machine_id: "",
                product_id: "",
                die_id: "",
                shift_id: "",
                notes: "",
                status: "planed",
                plan_date: "",
            });

        }

        setError("");

    }, [setup]);


    // =========================================================
    // MACHINE OPTIONS
    // =========================================================

    const machineOptions = (machines || [])
        .filter(item => item?.id != null)
        .map(item => ({
            value: Number(item.id),
            label:
                item.machine_code ||
                item.code ||
                item.name ||
                `Machine ${item.id}`,
        }));


    // =========================================================
    // PRODUCT OPTIONS
    // =========================================================

    const productOptions = (products || [])
        .filter(item => item?.id != null)
        .map(item => ({
            value: Number(item.id),
            label:
                item.name ||
                item.product_name ||
                item.product_code ||
                `Product ${item.id}`,
        }));


    // =========================================================
    // DIE OPTIONS
    // =========================================================

    const dieOptions = (dies || [])
        .filter(item => item?.id != null)
        .map(item => ({
            value: Number(item.id),
            label:
                item.type ||
                item.die_type ||
                item.name ||
                item.die_name ||
                `Die ${item.id}`,
        }));


    // =========================================================
    // STATIC SHIFT OPTIONS
    // =========================================================

    const shiftOptions = [
        {
            value: 1,
            label: "Shift-A",
        },
        {
            value: 2,
            label: "Shift-B",
        },
    ];


    // =========================================================
    // SELECT CHANGE
    // =========================================================

    const handleSelectChange = (field, option) => {

        setFormData(prev => ({
            ...prev,
            [field]: option
                ? option.value
                : "",
        }));

        setError("");

    };


    // =========================================================
    // INPUT CHANGE
    // =========================================================

    const handleInputChange = (e) => {

        const {
            name,
            value,
        } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        setError("");

    };


    // =========================================================
    // FIND SELECTED OPTION
    // =========================================================

    const getSelectedOption = (options, value) => {

        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return null;
        }

        return (
            options.find(
                option =>
                    Number(option.value) ===
                    Number(value)
            ) || null
        );

    };


    // =========================================================
    // VALIDATION
    // =========================================================

    const validateForm = () => {

        if (!formData.machine_id) {

            setError("Please select a machine.");

            return false;
        }


        if (!formData.product_id) {

            setError("Please select a product.");

            return false;
        }


        if (!formData.die_id) {

            setError("Please select a die.");

            return false;
        }


        if (!formData.shift_id) {

            setError("Please select a shift.");

            return false;
        }


        if (!formData.plan_date) {

            setError("Please select a plan date.");

            return false;
        }


        if (!formData.status) {

            setError("Please select status.");

            return false;
        }

     
        return true;

    };


    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();

        setError("");

        if (!validateForm()) {
            return;
        }


        const data = {

            machine_id: Number(
                formData.machine_id
            ),

            product_id: Number(
                formData.product_id
            ),

            die_id: Number(
                formData.die_id
            ),

            shift_id: Number(
                formData.shift_id
            ),

            notes: formData.notes?.trim() || "",

            status: formData.status,

            plan_date: formData.plan_date || null,

        };


        try {

            if (setup) {

                await onUpdate(
                    setup.id,
                    data
                );

            } else {

                await onCreate(
                    data
                );

            }

        } catch (error) {

            console.error(
                "PlanShiftModal submit error:",
                error
            );

            setError(
                error.response?.data?.message ||
                error.response?.data?.errors?.machine_id?.[0] ||
                error.response?.data?.errors?.product_id?.[0] ||
                error.response?.data?.errors?.die_id?.[0] ||
                error.response?.data?.errors?.shift_id?.[0] ||
                error.response?.data?.errors?.plan_date?.[0] ||
                error.message ||
                "Failed to save product machine setup."
            );

        }

    };


    // =========================================================
    // SELECT STYLES
    // =========================================================

    const selectStyles = {

        control: (base, state) => ({
            ...base,

            minHeight: "38px",

            borderColor: state.isFocused
                ? "#86b7fe"
                : "#dee2e6",

            boxShadow: state.isFocused
                ? "0 0 0 0.25rem rgba(13,110,253,.25)"
                : "none",

            "&:hover": {
                borderColor: "#86b7fe",
            },
        }),

        menu: base => ({
            ...base,
            zIndex: 9999,
        }),

        menuPortal: base => ({
            ...base,
            zIndex: 9999,
        }),

    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{
                backgroundColor: "rgba(0,0,0,0.5)",
            }}
        >

            <div className="modal-dialog modal-lg modal-dialog-centered">

                <div className="modal-content">


                    {/* =================================================
                        HEADER
                    ================================================= */}

                    <div className="modal-header">

                        <h5 className="modal-title">

                            {setup
                                ? "Edit Product Machine Setup"
                                : "Add Product Machine Setup"}

                        </h5>


                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={loading}
                        />

                    </div>


                    {/* =================================================
                        FORM
                    ================================================= */}

                    <form onSubmit={handleSubmit}>

                        <div className="modal-body">


                            {/* =================================================
                                ERROR
                            ================================================= */}

                            {error && (

                                <div className="alert alert-danger">

                                    {error}

                                </div>

                            )}


                            {/* =================================================
                                LOADING
                            ================================================= */}

                            {listLoading ? (

                                <div className="text-center py-4">

                                    <div
                                        className="spinner-border text-primary"
                                        role="status"
                                    />

                                    <div className="mt-2">
                                        Loading master data...
                                    </div>

                                </div>

                            ) : (

                                <div className="row">
                                    {/* =================================================
                                        MACHINE
                                    ================================================= */}
                                    <div className="col-md-3 mb-1">
                                        <label className="form-label">
                                            Machine
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </label>
                                        <Select
                                            options={machineOptions}
                                            value={getSelectedOption(
                                                machineOptions,
                                                formData.machine_id
                                            )}
                                            onChange={option =>
                                                handleSelectChange(
                                                    "machine_id",
                                                    option
                                                )
                                            }
                                            placeholder="Select Machine..."
                                            isSearchable
                                            isClearable
                                            isDisabled={loading}
                                            styles={selectStyles}
                                            menuPortalTarget={
                                                document.body
                                            }
                                            noOptionsMessage={() =>
                                                "No machines found"
                                            }
                                        />
                                    </div>
                                    {/* =================================================
                                        PRODUCT
                                    ================================================= */}
                                    <div className="col-md-3 mb-1">
                                        <label className="form-label">
                                            Product
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </label>
                                        <Select
                                            options={productOptions}
                                          value={getSelectedOption(
                                                productOptions,
                                                formData.product_id
                                            )}

                                            onChange={option =>
                                                handleSelectChange(
                                                    "product_id",
                                                    option
                                                )
                                            }

                                            placeholder="Select Product..."

                                            isSearchable
                                            isClearable

                                            isDisabled={loading}

                                            styles={selectStyles}

                                            menuPortalTarget={
                                                document.body
                                            }

                                            noOptionsMessage={() =>
                                                "No products found"
                                            }
                                        />

                                    </div>


                                    {/* =================================================
                                        DIE
                                    ================================================= */}

                                    <div className="col-md-3 mb-1">

                                        <label className="form-label">

                                            Die Capacity

                                            <span className="text-danger">
                                                *
                                            </span>

                                        </label>


                                        <Select
                                            options={dieOptions}

                                            value={getSelectedOption(
                                                dieOptions,
                                                formData.die_id
                                            )}

                                            onChange={option =>
                                                handleSelectChange(
                                                    "die_id",
                                                    option
                                                )
                                            }

                                            placeholder="Select Die..."

                                            isSearchable
                                            isClearable

                                            isDisabled={loading}

                                            styles={selectStyles}

                                            menuPortalTarget={
                                                document.body
                                            }

                                            noOptionsMessage={() =>
                                                "No dies found"
                                            }
                                        />

                                    </div>


                                    {/* =================================================
                                        SHIFT
                                    ================================================= */}

                                    <div className="col-md-3 mb-1">

                                        <label className="form-label">

                                            Shift

                                            <span className="text-danger">
                                                *
                                            </span>

                                        </label>


                                        <Select
                                            options={shiftOptions}

                                            value={getSelectedOption(
                                                shiftOptions,
                                                formData.shift_id
                                            )}

                                            onChange={option =>
                                                handleSelectChange(
                                                    "shift_id",
                                                    option
                                                )
                                            }

                                            placeholder="Select Shift"

                                            isSearchable
                                            isClearable

                                            isDisabled={loading}

                                            styles={selectStyles}

                                            menuPortalTarget={
                                                document.body
                                            }

                                            noOptionsMessage={() =>
                                                "No shifts found"
                                            }
                                        />

                                    </div>


                                    {/* =================================================
                                        STATUS
                                    ================================================= */}

                                    <div className="col-md-3 mb-1">

                                        <label className="form-label">

                                            Status

                                            <span className="text-danger">
                                                *
                                            </span>

                                        </label>


                                        <select
                                            name="status"
                                            className="form-select"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        >

                                            <option value="planed">
                                                Planned
                                            </option>

                                            <option value="saved">
                                                Saved
                                            </option>

                                        </select>

                                    </div>

 {/* =================================================
                                        PLAN DATE
                                    ================================================= */}
                                    <div className="col-md-3 mb-1">
                                        <label className="form-label">
                                            Select Plan Date
                                          <span className="text-danger">
                                                *
                                            </span>
                                        </label>
                                        <input
                                        type="date"
                                        name="plan_date"
                                        className="form-control"
                                        value={formData.plan_date || ""}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                    />

                                    </div>

                                    {/* =================================================
                                        NOTES
                                    ================================================= */}

                                    <div className="col-md-6 mb-1">

                                        <label className="form-label">
                                            Notes
                                        </label>


                                        <textarea
                                            name="notes"
                                            className="form-control"
                                            rows="1"
                                            value={formData.notes}
                                            onChange={handleInputChange}
                                            placeholder="Enter notes"
                                            disabled={loading}
                                        />
                                    </div>
                                   

                                </div>

                            )}

                        </div>


                        {/* =================================================
                            FOOTER
                        ================================================= */}

                        <div className="modal-footer">

                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>


                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={
                                    loading ||
                                    listLoading
                                }
                            >

                                {loading ? (

                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                        />

                                        Saving...
                                    </>

                                ) : (

                                    setup
                                        ? "Update"
                                        : "Save"

                                )}

                            </button>

                        </div>

                    </form>

                </div>

            </div>

        </div>

    );

};


export default PlanShiftModal;



